import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BioweaveAuthError } from '@bioweave/api-client/errors';
import { authStore } from '@/lib/authStore';
import { setAuthTokenReader } from '@/lib/authToken';
import { notifyAuthExpired, notifySessionRenewed, onAuthExpired, onAuthRecover, type RecoveryOutcome } from '@/lib/authExpiry';
import { CognitoAuthError, cognitoPool, refreshStore, refreshWith, revoke, type Tokens } from '@/lib/cognito';
import { consoleApi, UnlinkedSignInError } from '@/lib/consoleApi';

/** How this session signed in: a pasted bearer token (until the cutover), or Cognito. */
type Via = 'token' | 'cognito';

type AuthState = {
  token: string | null; userRef: string | null; role: string | null; isReady: boolean;
  via: Via | null;
  /** A Cognito sign-in that worked but is not linked to an account yet. */
  unlinked: boolean;
};
type AuthContextValue = AuthState & {
  signIn: (v: { token: string; userRef: string; role: string }) => void;
  /** After the code is accepted: verify the access token with core and start the session. */
  signInWithCognito: (tokens: Tokens) => Promise<'signed-in' | 'unlinked'>;
  signOut: () => void;
  cognitoEnabled: boolean;
};

/** What one refresh came to: a new token; kept through a blip (a retry is scheduled);
 *  the session ended (wiped); or stale (a newer session owns the state, so nothing). */
type RefreshOutcome = 'refreshed' | 'kept' | 'ended' | 'stale';

const SIGNED_OUT: AuthState = { token: null, userRef: null, role: null, isReady: true, via: null, unlinked: false };
/** Refresh this long before the access token expires (it lasts an hour, D7). */
const REFRESH_AHEAD_MS = 2 * 60 * 1000;
/** After a refresh that could not reach Cognito (network, 5xx). */
const TRANSIENT_RETRY_MS = 30 * 1000;

/** At refresh, only network and 5xx are transient: anything else Cognito says means the
 *  refresh token will never work again (Tiny's module finding). */
function refreshRefused(e: unknown): boolean {
  return !(e instanceof CognitoAuthError && e.kind === 'unavailable');
}
/** At core's verify, a refusal is core refusing the token (or an unlinked sign-in); a
 *  network or 5xx failure is transient. */
function verifyRefused(e: unknown): boolean {
  return e instanceof BioweaveAuthError || e instanceof UnlinkedSignInError;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...SIGNED_OUT, isReady: false });
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const tokenRef = useRef<string | null>(null);
  const pool = useMemo(() => cognitoPool(), []);
  // SIGN-OUT WINS. Every sign-out, wipe and pasted-token sign-in starts a new
  // generation; a Cognito step that resolves into an older one saves nothing and revokes
  // what it got.
  const generation = useRef(0);
  // One refresh at a time (timer, tab back, a 401's recovery, the retry), so a rotation
  // is never spent twice; one retry timer, never stacked.
  const refreshing = useRef<Promise<RefreshOutcome> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A refresh failed on a blip since the last good one: the next good one tells App, so
  // the queries that failed meanwhile run again (Tiny's follow-up 2).
  const blipped = useRef(false);
  const clearRetry = () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
    blipped.current = false;
  };

  useEffect(() => { setAuthTokenReader(() => tokenRef.current); }, []);
  useEffect(() => { tokenRef.current = state.token; }, [state.token]);
  useEffect(() => () => clearRetry(), []);
  // A wiped session (authExpiry): bring the in-memory session into line, so
  // RequireAdvisor routes to /sign-in through the normal gate.
  useEffect(() => onAuthExpired(() => {
    generation.current += 1;
    clearRetry();
    tokenRef.current = null;
    setExpiresAt(null);
    setState(SIGNED_OUT);
  }), []);

  /** A Cognito access token, verified by core, becomes the session. `gen` is the
   *  generation the caller started in (restore passes its own). The rotated refresh token
   *  is kept BEFORE the verify, so a blip there does not spend the session. */
  const adopt = useCallback(async (tokens: Tokens, gen: number): Promise<'signed-in' | 'unlinked'> => {
    const stale = () => {
      if (pool && tokens.refreshToken) void revoke(pool, tokens.refreshToken);
    };
    if (gen !== generation.current) { stale(); return 'signed-in'; }
    if (tokens.refreshToken) refreshStore.save(tokens.refreshToken);
    try {
      const who = await consoleApi.authVerifyAccess(tokens.accessToken);
      if (gen !== generation.current) { stale(); return 'signed-in'; }
      tokenRef.current = tokens.accessToken;
      setExpiresAt(tokens.expiresAt);
      setState({ token: tokens.accessToken, userRef: who.user_ref, role: who.role, isReady: true,
                 via: 'cognito', unlinked: false });
      return 'signed-in';
    } catch (e) {
      if (gen !== generation.current) { stale(); throw e; }
      tokenRef.current = null;
      setExpiresAt(null);
      if (verifyRefused(e)) refreshStore.clear();   // a transient failure keeps it for later
      if (e instanceof UnlinkedSignInError) {
        setState({ ...SIGNED_OUT, unlinked: true });
        return 'unlinked';
      }
      setState(SIGNED_OUT);
      throw e;
    }
  }, [pool]);

  const signInWithCognito = useCallback((tokens: Tokens) => adopt(tokens, generation.current), [adopt]);

  // Restore: a pasted token as before; else a Cognito session from its refresh token.
  // Once per mount: StrictMode runs effects twice, and a second refresh would spend the
  // rotated token. The generation is taken before the refresh and carried into adopt,
  // so a token pasted meanwhile wins (Tiny's D1-b).
  const restoreStarted = useRef(false);
  useEffect(() => {
    if (restoreStarted.current) return;
    restoreStarted.current = true;
    const stored = authStore.load();
    if (stored) {
      tokenRef.current = stored.token;
      setState({ token: stored.token, userRef: stored.userRef, role: stored.role, isReady: true,
                 via: 'token', unlinked: false });
      return;
    }
    const refresh = pool ? refreshStore.load() : null;
    if (!pool || !refresh) { setState(SIGNED_OUT); return; }
    const gen = generation.current;
    refreshWith(pool, refresh)
      .then((t) => adopt(t, gen))
      .catch((e) => {
        if (gen !== generation.current) return;
        if (e instanceof CognitoAuthError ? refreshRefused(e) : verifyRefused(e)) refreshStore.clear();
        setState((s) => (s.isReady ? s : SIGNED_OUT));
      });
  }, [pool, adopt]);

  const refreshNow = useCallback((): Promise<RefreshOutcome> => {
    if (refreshing.current) return refreshing.current;
    const run = (async (): Promise<RefreshOutcome> => {
      const refresh = refreshStore.load();
      if (!pool || !refresh) {
        // A Cognito session with nothing to refresh from cannot go on: wipe it, though
        // nothing is stored for notifyAuthExpired to find (Tiny's follow-up 3).
        notifyAuthExpired({ live: true });
        return 'ended';
      }
      const gen = generation.current;
      try {
        const t = await refreshWith(pool, refresh);
        if (gen !== generation.current) {
          // Signed out (or a token pasted) while this was in flight: save nothing, and
          // revoke the rotated token so no live refresh token outlives the change.
          if (t.refreshToken && t.refreshToken !== refresh) void revoke(pool, t.refreshToken);
          return 'stale';
        }
        // Rotation is on: the refresh token handed back is the only one that works next time.
        if (t.refreshToken && t.refreshToken !== refresh) refreshStore.save(t.refreshToken);
        const afterBlip = blipped.current;
        clearRetry();
        tokenRef.current = t.accessToken;
        setExpiresAt(t.expiresAt);
        setState((s) => (s.via === 'cognito' ? { ...s, token: t.accessToken } : s));
        if (afterBlip) notifySessionRenewed();
        return 'refreshed';
      } catch (e) {
        if (gen !== generation.current) return 'stale';
        if (refreshRefused(e)) {
          notifyAuthExpired();
          return 'ended';
        }
        // Network or service trouble: keep the session, one retry, not a stack of them.
        clearRetry();
        blipped.current = true;
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null;
          if (gen === generation.current) void refreshNow();
        }, TRANSIENT_RETRY_MS);
        return 'kept';
      }
    })().finally(() => { refreshing.current = null; });
    refreshing.current = run;
    return run;
  }, [pool]);

  // A 401 on a COGNITO session tries this before anything is wiped (D2). Registered only
  // while the session signed in through Cognito: a pasted-token 401 always wipes. It is
  // 'recovered' only when core ACCEPTS the new access token (Tiny's L1), and a blip is
  // 'kept', never a refetch with a token that is still expired (L2).
  // First core is asked about the token the 401 came back on: if core accepts it, the 401
  // was not about the session (a route's own refusal), so no rotation is spent and nothing
  // counts toward the cap (Tiny's follow-up 4).
  const recover = useCallback(async (): Promise<RecoveryOutcome> => {
    const gen = generation.current;
    const current = tokenRef.current;
    if (current) {
      try {
        await consoleApi.authVerifyAccess(current);
        return 'kept';
      } catch {
        // Refused: the token is spent, refresh. Core unreachable: refresh as before.
        if (gen !== generation.current) return 'kept';
      }
    }
    const outcome = await refreshNow();
    if (outcome === 'ended') return 'ended';
    if (outcome !== 'refreshed' || gen !== generation.current) return 'kept';
    const token = tokenRef.current;
    if (!token) return 'kept';
    try {
      await consoleApi.authVerifyAccess(token);
      return gen === generation.current ? 'recovered' : 'kept';
    } catch (e) {
      if (gen !== generation.current) return 'kept';
      if (verifyRefused(e)) {
        notifyAuthExpired();
        return 'ended';
      }
      return 'kept';
    }
  }, [refreshNow]);
  useEffect(() => (state.via === 'cognito' ? onAuthRecover(recover) : undefined), [state.via, recover]);

  // Keep a Cognito session's access token fresh: a timer ahead of expiry, and a check
  // when the tab comes back (a laptop asleep past the timer).
  useEffect(() => {
    if (state.via !== 'cognito' || expiresAt === null) return;
    const wait = Math.max(expiresAt - Date.now() - REFRESH_AHEAD_MS, 5_000);
    const timer = setTimeout(() => void refreshNow(), wait);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && expiresAt - Date.now() < REFRESH_AHEAD_MS) void refreshNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [state.via, expiresAt, refreshNow]);

  const signIn = useCallback((v: { token: string; userRef: string; role: string }) => {
    // A pasted token replaces any Cognito session outright, and revokes the refresh token
    // it replaces so no live one outlives the change.
    generation.current += 1;
    clearRetry();
    const replaced = refreshStore.load();
    refreshStore.clear();
    if (pool && replaced) void revoke(pool, replaced);
    authStore.save(v);
    // Synchronously, not through the effect: the page navigated to next renders and
    // fetches before this provider's effects run, so its first request would leave with
    // no token and 401.
    tokenRef.current = v.token;
    setState({ token: v.token, userRef: v.userRef, role: v.role, isReady: true, via: 'token', unlinked: false });
  }, [pool]);

  const signOut = useCallback(() => {
    generation.current += 1;
    clearRetry();
    const refresh = refreshStore.load();
    authStore.clear();
    refreshStore.clear();
    tokenRef.current = null;
    setExpiresAt(null);
    setState(SIGNED_OUT);
    // Revoke the refresh token (and the access tokens it issued) at Cognito. Best
    // effort and after the local clear: a failed revoke must not keep anyone signed in.
    if (pool && refresh) void revoke(pool, refresh);
  }, [pool]);

  const value = useMemo<AuthContextValue>(() => ({
    ...state, signIn, signInWithCognito, signOut, cognitoEnabled: pool !== null,
  }), [state, signIn, signInWithCognito, signOut, pool]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
